<template>
  <v-form ref="DEMANDE_ATTESTATION" @submit.prevent="sectionSave" lazy-validation class="section">
    <div>
      <section-header v-if="!hideTitle"
                      :ressource="ressource"
                      :isExtended="extended"
                      :dialog="dialog"
                      :picto="picto()"
                      :fiche=fiche
                      :isAbstract=isAbstract
                      @extended=setExtended
                      @closeDialog=closeDialog
                      ref="DEMANDE_ATTESTATION">
      </section-header>
      <div v-if="extended" id="field-container" :class="dialog ? 'p-0 p-md-3' : 'py-2'">
        <etablissement-select v-if="isDisplayed('ETABLISSEMENT_ID')"
                              ref="ETABLISSEMENT_ID"
                              :theme="theme"
                              :showMissing="showMissing"
                              v-model:value=etablissementId
                              :moduleId=moduleId
                              libelle="'Etablissement'"
                              newLabel=''
                              selectText=""
                              @status=statusEtablissementId
                              :requiredAttributs="requiredAttributs"
                              :autoSelect=true
                              :ressource=ressourceEtablissementSelect
                              :filters=etablissementIdFilter
                              :toggleReset=resetEtablissementId
                              :required="isRequired('ETABLISSEMENT_ID')"
                              :contextId=attestation.id
                              :readOnly="readOnly('ETABLISSEMENT_ID')"
                              :objType=objType
                              :fiche=fiche
                              :sectionName="sectionName">
        </etablissement-select>
        <attestation-def-select v-if="isDisplayed('ATTESTATION_DEF_ID')"
                                ref="ATTESTATION_DEF_ID"
                                :theme="theme"
                                :showMissing="showMissing"
                                v-model:value=attestationDefId
                                :moduleId=moduleId
                                libelle="'Organisme'"
                                newLabel=''
                                selectText=""
                                @status=statusAttestationDefId
                                :requiredAttributs="requiredAttributs"
                                :autoSelect=true
                                :ressource=ressourceAttestationDefSelect
                                :filters=attestationDefIdFilter
                                :toggleReset=resetAttestationDefId
                                :required="isRequired('ATTESTATION_DEF_ID')"
                                :contextId=attestation.id
                                :readOnly="readOnly('ATTESTATION_DEF_ID')"
                                :objType=objType
                                :fiche=fiche
                                :sectionName="sectionName">
        </attestation-def-select>
        <attestation-date-demande v-if="isDisplayed('DATE_DEMANDE')"
                                  ref="DATE_DEMANDE"
                                  :theme="theme"
                                  :showMissing="showMissing"
                                  v-model:value=dateDemande
                                  @status=statusDateDemande
                                  :periods='periodDateDemande'
                                  :required="isRequired('DATE_DEMANDE')"
                                  :contextId=attestation.id
                                  :readOnly="readOnly('DATE_DEMANDE')"
                                  :objType=objType
                                  :fiche=fiche
                                  :sectionName="sectionName">
        </attestation-date-demande>
        <attestation-statut v-if="isDisplayed('STATUT')"
                            ref="STATUT"
                            :theme="theme"
                            :showMissing="showMissing"
                            v-model:value=statut
                            @status=statusStatut
                            :required="isRequired('STATUT')"
                            :contextId=attestation.id
                            :readOnly="readOnly('STATUT')"
                            :objType=objType
                            :fiche=fiche
                            :sectionName="sectionName">
        </attestation-statut>
        <attestation-date-valid v-if="isDisplayed('DATE_VALID')"
                                ref="DATE_VALID"
                                :theme="theme"
                                :showMissing="showMissing"
                                v-model:value=dateValid
                                @status=statusDateValid
                                :periods='periodDateValid'
                                :required="isRequired('DATE_VALID')"
                                :contextId=attestation.id
                                :readOnly="readOnly('DATE_VALID')"
                                :objType=objType
                                :fiche=fiche
                                :sectionName="sectionName">
        </attestation-date-valid>
        <attestation-motif v-if="isDisplayed('MOTIF')"
                           ref="MOTIF"
                           :theme="theme"
                           :showMissing="showMissing"
                           v-model:value=motif
                           @status=statusMotif
                           :over-ride-placeholder=placeholderMotif
                           :required="isRequired('MOTIF')"
                           :contextId=attestation.id
                           :readOnly="readOnly('MOTIF')"
                           :objType=objType
                           :fiche=fiche
                           :sectionName="sectionName">
        </attestation-motif>
      </div>
    </div>
    <div v-if="extended" class="d-flex flex-row-reverse">
      <loader-button v-if="saveButton"
                     class="ms-2"
                     type="submit"
                     :loading="saveLoading"
                     :label="saveLabel"
                     :disabled="!updatedAndValid">
      </loader-button>
      <div v-for="(button, index) in extraButtons" :key="index">
        <loader-button class="ms-2"
                       :outlined="button.outlined"
                       @callback="button.callback()"
                       :type="button.type"
                       :label="button.label"
                       :loading="button.loading"
                       :disabled="button.disabled">
        </loader-button>
      </div>
    </div>
    <div class="d-flex flex-row-reverse" v-if="saveFailed && saveButton && extended">
      <p class="text-12 text-bold error-text">{{errorTxt}}</p>
    </div>
  </v-form>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import CrudAttestation from '@/generated/cruds/CrudAttestation';
  // eslint-disable-next-line
  import { GenControl, GenSection, RessourceGenControl, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import { FwkButtons, RessourceFwkButton, RessourceFwkSection, FwkSections } from '@/ressources/globalRessources';
  import { Sections } from '@/generated/classes/AppSchema';
  import Button, { ButtonCallback } from '@/framework/base/classes/Button';
  import { ColumnFilter, ListChoix } from '@/extend/classes/ListData';
  import { ObjTypes } from '@/generated/classes/ObjTypes';

  export default defineComponent({
    extends:  CrudAttestation,
    components: {},
    props: {
      hideTitle: {
        type: Boolean,
      },
      isAbstract: {
        type: Boolean,
      },
    },

    data() {
      return {
        sectionName: Sections.DEMANDE_ATTESTATION,
        displayMap: new Map<string, Boolean>,
        readOnlyMap: new Map<string, Boolean>,
        requiredMap: new Map<string, Boolean>,
        placeholderMotif: null,
      }
    },

    computed: {
      saveLabel () { 
        return RessourceFwkButton(FwkButtons.ENREGISTRER).label;
      },

      errorTxt () { 
        return RessourceFwkSection(FwkSections.ERROR_TEXT).label;
      },

      ressourceEtablissementSelect () { return RessourceGenControl(GenControl.DEMANDE_ATTESTATION_ETABLISSEMENT_SELECT); },
      ressourceAttestationDefSelect () { return RessourceGenControl(GenControl.DEMANDE_ATTESTATION_ATTESTATION_DEF_SELECT); },

      ressource () { return RessourceGenSection(GenSection.DEMANDE_ATTESTATION); },

      fields (): string[] {
        return ['ETABLISSEMENT_ID', 'ATTESTATION_DEF_ID', 'DATE_DEMANDE', 'STATUT', 'DATE_VALID', 'MOTIF'];
      },

      extraButtons (): Button[] {
        return [];
      },

      etablissementIdFilter (): ColumnFilter[] {
        const res: ColumnFilter[] = [];
        return res;
      },
      attestationDefIdFilter (): ColumnFilter[] {
        const res: ColumnFilter[] = [];
        return res;
      },
    },
    methods: {
      getRequiredAttributs(): string[] {
        const res: string[] = [];
        if (this.requiredAttributs) {
          this.requiredAttributs.forEach((attrib) => {
            const value = attrib.split(".");
            if (value[0] == this.sectionName) {
              res.push(value[1])
            }
          })
        }
        return res;
      },

      requiredMissing(requiredAttributs: string[], value: string): boolean {
        return requiredAttributs.length > 0 && requiredAttributs.includes(value);;
      },

      readOnly (value: string): boolean {
        let res = false;
        if (!this.editAttestation.canUpdate) {
          return true;
        }
        if (this.readOnlyMap.size == 0) {
          this.readOnlyMap.set("ETABLISSEMENT_ID", false);
          this.readOnlyMap.set("ATTESTATION_DEF_ID", false);
          this.readOnlyMap.set("DATE_DEMANDE", false);
          this.readOnlyMap.set("STATUT", false);
          this.readOnlyMap.set("DATE_VALID", false);
          this.readOnlyMap.set("MOTIF", false);
        }
        const readOnly = this.readOnlyMap.get(value);
        if (readOnly != undefined) {
          res = readOnly;
        }
        return res;
      },

      isDisplayed (value: string): boolean {
        let res = true;
        if (this.displayMap.size == 0) {
        const requiredAttributs = this.getRequiredAttributs();
          this.displayMap.set("ETABLISSEMENT_ID", true);
          this.displayMap.set("ATTESTATION_DEF_ID", true);
          this.displayMap.set("DATE_DEMANDE", true);
          this.displayMap.set("STATUT", true);
          this.displayMap.set("DATE_VALID", true);
          this.displayMap.set("MOTIF", true);
        }
        const display = this.displayMap.get(value);
        if (display != undefined) {
          res = display;
        }
        return res;
      },

      isRequired (value: string): boolean {
        let res = true;
        const requiredAttributs = this.getRequiredAttributs();
        if (this.requiredMap.size == 0) {
          this.requiredMap.set("ETABLISSEMENT_ID", true);
          this.requiredMap.set("ATTESTATION_DEF_ID", true);
          this.requiredMap.set("DATE_DEMANDE", true);
          this.requiredMap.set("STATUT", true);
          this.requiredMap.set("DATE_VALID", true);
          this.requiredMap.set("MOTIF", true);
        }
        const required = this.requiredMap.get(value);
        if (required != undefined) {
          res = required;
        }
        return res;
      },

      picto (): string {
        return 'SectionPictoAttestation' 
      },

      sectionSave (): void {
        return this.save();
      }
    }
  })
</script>
